// Shared helpers for reading the Scrapy metadata JSON blob (`item.metadata` on `Item` /
// `QaReviewItem` — both are plain `Record<string, unknown>` with the same `Fields` array shape)
// used by both the investigations grid/list views and the Weekly QA review modal.

export type MetadataField = {
  FieldName: string;
  Content: unknown;
  Index?: number;
  IsMetadata?: boolean;
  Type?: string;
};

// Loose on purpose: fits `Item.metadata` and `QaReviewItem.metadata` (both `Record<string,
// unknown>`), and tolerates a missing/nullish metadata object.
export type ItemMetadataLike = Record<string, unknown> | null | undefined;

export function getMetaFields(metadata: ItemMetadataLike): MetadataField[] {
  return (metadata?.Fields as MetadataField[] | undefined) ?? [];
}

export function getMetaField(metadata: ItemMetadataLike, fieldName: string): string {
  const fields = getMetaFields(metadata);
  return String(fields.find((f) => f.FieldName === fieldName)?.Content ?? '');
}

export function getMetaFieldAny(metadata: ItemMetadataLike, candidateFieldNames: string[]): string {
  const fields = getMetaFields(metadata);
  const match = fields.find((f) => candidateFieldNames.includes(f.FieldName.toLowerCase()));
  return match ? String(match.Content ?? '') : '';
}

export function getMetaBoolean(metadata: ItemMetadataLike, candidateFieldNames: string[]): boolean {
  const fields = getMetaFields(metadata);
  return fields.some(
    (f) => candidateFieldNames.includes(f.FieldName.toLowerCase()) && f.Content === true
  );
}

// A video's own "Is monetized" is only "ads served" - see ItemsController.MonetizedFieldNames.
export function isChannelMonetized(metadata: ItemMetadataLike, isActor: boolean): boolean {
  return getMetaBoolean(metadata, isActor ? MONETIZED_FIELD_NAMES : ['is channel monetized']);
}

// Video-level `defaultAudioLanguage` wins over `defaultLanguage` — the audio track's actual
// language is a better signal than the metadata language, which YouTube often defaults to the
// uploader's locale (e.g. "en-US") even when the video itself is in another language. (Scrapy
// renders an absent value as the literal string "Not specified", not empty, so that has to be
// filtered too.)
export function getAutoSelectedLanguageCode(metadata: ItemMetadataLike): string | null {
  const primary = getMetaFieldAny(metadata, DEFAULT_AUDIO_LANGUAGE_FIELD_NAMES);
  if (primary && primary.toLowerCase() !== 'not specified') {
    return primary;
  }

  const fallback = getMetaFieldAny(metadata, DEFAULT_LANGUAGE_FIELD_NAMES);
  if (fallback && fallback.toLowerCase() !== 'not specified') {
    return fallback;
  }

  return null;
}

// Base BCP-47 subtag ("en" from "en-US") of an item's declared audio/metadata language, or null
// when undeclared. Shared by ClassifyDialog's language auto-select (which does this split inline)
// and the bulk translate skip in InvestigationsWorkspace - both only care about the base language,
// not region/script variants.
export function getDeclaredLanguageSubtag(metadata: ItemMetadataLike): string | null {
  const code = getAutoSelectedLanguageCode(metadata);
  return code ? code.toLowerCase().split(/[-_]/)[0]! : null;
}

// A video's Description, or a Pinterest (Falcon) profile's Bio - the same free-text "about this
// item" slot, so a profile card shows its bio where a video card shows its description.
export function getDescription(metadata: ItemMetadataLike): string {
  return getMetaField(metadata, 'Description') || getMetaField(metadata, 'Bio');
}

// Pinterest (Falcon) pins serialise Tags as a Python list repr - "['Stoneware Mug', \"It's\"]" - so
// a bare comma split leaves the brackets and quotes on the pills and splits a tag containing a comma.
// Returns null when the value is not a well-formed list literal, so the caller can fall back.
function parsePythonStringList(raw: string): string[] | null {
  const text = raw.trim();
  if (!text.startsWith('[') || !text.endsWith(']')) {
    return null;
  }

  const items: string[] = [];
  let i = 1;
  const end = text.length - 1;
  while (i < end) {
    const quote = text[i]!;
    if (quote === ',' || /\s/.test(quote)) {
      i++;
      continue;
    }

    if (quote !== "'" && quote !== '"') {
      return null;
    }

    let value = '';
    let closed = false;
    i++;
    while (i < end) {
      const c = text[i]!;
      if (c === '\\' && i + 1 < end) {
        value += text[i + 1]!;
        i += 2;
      } else if (c === quote) {
        closed = true;
        i++;
        break;
      } else {
        value += c;
        i++;
      }
    }

    if (!closed) {
      return null;
    }

    items.push(value);
  }

  return items;
}

// The Tags metadata field is either one comma-joined string ("dogs,training,pets", YouTube) or a
// Python list repr (Pinterest) - split into individual tags for both display (ItemMeta's tag pills)
// and translation. Each tag is translated as its own segment rather than the joined blob as a
// whole: Cloud Translation doesn't promise to preserve a delimiter it has no reason to treat as
// meaningful, so a joined-then-resplit round trip can merge two tags into one pill or split one tag
// into two.
export function getTags(metadata: ItemMetadataLike): string[] {
  const raw = getMetaField(metadata, 'Tags');
  if (!raw) {
    return [];
  }

  const items = parsePythonStringList(raw) ?? raw.split(',');
  return items.map((t) => t.trim()).filter(Boolean);
}

// Every free-text field the translate-on-demand feature sends to the API for one item - title
// (a top-level Item/QaReviewItem field, not in metadata), the description (see getDescription),
// and each tag as its own segment (see getTags). Blank entries are dropped so the caller never
// sends (or bills for) empty segments.
export function getTranslatableSegments(
  title: string | null | undefined,
  metadata: ItemMetadataLike
): string[] {
  return [title ?? '', getDescription(metadata), ...getTags(metadata)].filter(
    (segment) => segment.trim() !== ''
  );
}

// Hoisted: constructing an Intl.NumberFormat is expensive, and this ran per-call - up to
// several times per item card (subscribers, views, likes) on every render of every card.
const compactNumberFormatter = new Intl.NumberFormat('en', {notation: 'compact'});

export function formatCompactNumber(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  const n = Number(raw);
  if (Number.isNaN(n)) {
    return null;
  }

  return compactNumberFormatter.format(n);
}

// FieldName aliases per crisp_scrapy_common/models/fields.py; matched case-insensitively.
export const AGE_RESTRICTED_FIELD_NAMES = ['age restricted'];
export const MONETIZED_FIELD_NAMES = ['is channel monetized', 'is monetized'];
export const VERIFIED_FIELD_NAMES = ['verified', 'is verified', 'profile is verified'];
// Pinterest's (Falcon's) merchant-programme badge - a different claim from the identity
// verification above, so it gets its own icon rather than counting as verified.
export const VERIFIED_MERCHANT_FIELD_NAMES = ['is verified merchant'];
export const DURATION_FIELD_NAMES = ['duration'];
export const VIEW_COUNT_FIELD_NAMES = ['view count', 'viewcount'];
export const LIKE_COUNT_FIELD_NAMES = ['likes', 'like count', 'likecount'];
export const COMMENT_COUNT_FIELD_NAMES = ['comment count', 'commentcount'];
export const CATEGORY_FIELD_NAMES = ['category name'];
// A Pinterest pin's engagement count (Falcon). Pinterest has no likes, and a profile carries neither.
export const SAVE_COUNT_FIELD_NAMES = ['save count'];
export const LANGUAGE_FIELD_NAMES = ['language', 'default language', 'default audio language'];
// Split out from LANGUAGE_FIELD_NAMES for auto-select: that constant collapses both into one
// display value, but auto-select needs to know which of the two actually matched.
export const DEFAULT_LANGUAGE_FIELD_NAMES = ['default language'];
export const DEFAULT_AUDIO_LANGUAGE_FIELD_NAMES = ['default audio language'];
export const MADE_FOR_KIDS_FIELD_NAMES = ['made for kids'];
// The platform's own "safe for minors" signal (YouTube's undocumented contentDetails.inBucket,
// scrapy Fields.SAFE_FOR_MINORS) - absent means the platform didn't report it, not "unsafe".
export const SAFE_FOR_MINORS_FIELD_NAMES = ['safe for minors'];
export const LIVESTREAM_FIELD_NAMES = ['islivestream'];
export const MATURE_FIELD_NAMES = ['mature content', 'is nsfw'];
export const CONTENT_WARNING_FIELD_NAMES = ['content warning'];
export const CONTENT_WARNING_REASON_FIELD_NAMES = ['content warning reason'];

// Channel-only fields (YoutubeChannelModel.build_channel_model); a video item never carries these.
export const SUBSCRIBER_FIELD_NAMES = ['subscribers', 'subscriber count'];
// Pinterest's (Falcon's) name for the same audience-size concept as a YouTube subscriber count
// (scrapy Fields.FOLLOWER_COUNT).
export const FOLLOWER_FIELD_NAMES = ['followers count'];
export const CHANNEL_VIDEO_COUNT_FIELD_NAMES = ['video count'];
export const JOINED_DATE_FIELD_NAMES = ['joined date'];
export const LAST_POST_DATE_FIELD_NAMES = [
  'date of last post',
  'last post date',
  'last pin saved time'
];
// 'country' is the channel item's own field (YoutubeChannelModel); 'channel location' is a video
// item's copy of its channel's country (scrapy PR #29, Fields.CHANNEL_LOCATION) - same concept,
// two different FieldNames depending on whether the item IS the channel or just references one.
export const COUNTRY_FIELD_NAMES = ['country', 'channel location'];

export const KNOWN_FIELD_NAMES = new Set([
  'description',
  'bio',
  'tags',
  ...AGE_RESTRICTED_FIELD_NAMES,
  ...MONETIZED_FIELD_NAMES,
  ...VERIFIED_FIELD_NAMES,
  ...VERIFIED_MERCHANT_FIELD_NAMES,
  ...DURATION_FIELD_NAMES,
  ...VIEW_COUNT_FIELD_NAMES,
  ...LIKE_COUNT_FIELD_NAMES,
  ...COMMENT_COUNT_FIELD_NAMES,
  ...CATEGORY_FIELD_NAMES,
  ...SAVE_COUNT_FIELD_NAMES,
  ...LANGUAGE_FIELD_NAMES,
  ...MADE_FOR_KIDS_FIELD_NAMES,
  ...SAFE_FOR_MINORS_FIELD_NAMES,
  ...LIVESTREAM_FIELD_NAMES,
  ...MATURE_FIELD_NAMES,
  ...CONTENT_WARNING_FIELD_NAMES,
  ...CONTENT_WARNING_REASON_FIELD_NAMES,
  ...SUBSCRIBER_FIELD_NAMES,
  ...FOLLOWER_FIELD_NAMES,
  ...CHANNEL_VIDEO_COUNT_FIELD_NAMES,
  ...JOINED_DATE_FIELD_NAMES,
  ...LAST_POST_DATE_FIELD_NAMES,
  ...COUNTRY_FIELD_NAMES
]);
